import { maxMatchService } from '@/entities/max-match/api/max-match.api';
import type {
  MaxMatchMiniMrCreatePayload,
  MaxMatchMiniMrUpdatePayload,
} from '@/entities/max-match/api/max-match.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const maxMatchKeys = {
  all: ['max-match'] as const,
  searchLists: () => [...maxMatchKeys.all, 'search'] as const,
  /** search/pageable은 usePaginatedQuery가 queryKey에 추가한다 */
  userDetails: (userId: number) =>
    [...maxMatchKeys.all, 'user-details', userId] as const,
  userClinics: (userId: number) =>
    [...maxMatchKeys.all, 'user-clinics', userId] as const,
  operating: () => [...maxMatchKeys.all, 'operating'] as const,
  miniLists: () => [...maxMatchKeys.all, 'mini'] as const,
  miniImages: (summaryId: number) =>
    [...maxMatchKeys.all, 'mini-images', summaryId] as const,
  miniMrs: (imageId: number) =>
    [...maxMatchKeys.all, 'mini-mrs', imageId] as const,
};

export const useMaxMatchList = (
  search: Record<string, string | number | undefined>,
  pageable: Pageable,
) =>
  usePaginatedQuery({
    queryKeyPrefix: maxMatchKeys.searchLists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) => maxMatchService.search(s, p),
  });

export const useMaxMatchUserDetails = (
  userId: number,
  clinicId: string,
  pageable: Pageable,
) =>
  useQuery({
    // 객체(search/pageable)를 key에 넣지 않는다 — 참조 불안정으로 무한 refetch 방지
    queryKey: [
      ...maxMatchKeys.userDetails(userId),
      clinicId,
      pageable.page,
      pageable.size,
    ],
    queryFn: () =>
      maxMatchService.getUserDetails(
        userId,
        clinicId ? { clinicId } : {},
        pageable,
      ),
    enabled: userId > 0,
  });

export const useMaxMatchUserClinics = (userId: number) =>
  useQuery({
    queryKey: maxMatchKeys.userClinics(userId),
    queryFn: () => maxMatchService.getUserClinics(userId),
    enabled: userId > 0,
  });

export const useRunMaxMatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prescriptionMonth: string) =>
      maxMatchService.runForMonth(prescriptionMonth),
    // 생성은 비멱등 POST — 타임아웃 후 자동 재시도하면 서버에서 중복 실행된다
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.all });
    },
  });
};

export const usePublishMaxMatch = () =>
  useMutation({
    mutationFn: (prescriptionYearMonth: string) =>
      maxMatchService.publish(prescriptionYearMonth),
  });

export const useMaxMatchExists = (prescriptionYearMonth: string) =>
  useQuery({
    queryKey: [...maxMatchKeys.all, 'exists', prescriptionYearMonth],
    queryFn: () => maxMatchService.getExists(prescriptionYearMonth),
    enabled: Boolean(prescriptionYearMonth),
  });

export const useMaxMatchOperatingSettings = () =>
  useQuery({
    queryKey: maxMatchKeys.operating(),
    queryFn: async () => {
      const [operatingHours, screenWindows] = await Promise.all([
        maxMatchService.getOperatingHours(),
        maxMatchService.getScreenWindows(),
      ]);
      return { operatingHours, screenWindows };
    },
  });

export const useSaveMaxMatchScreenWindows = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      requests: { screenType: string; payload: Record<string, unknown> }[],
    ) =>
      Promise.all(
        requests.map(({ screenType, payload }) =>
          maxMatchService.saveScreenWindow(screenType, payload),
        ),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.operating() });
    },
  });
};

export const useMaxMatchMiniList = (
  search: Record<string, string | number | boolean | undefined>,
  pageable: Pageable,
) =>
  usePaginatedQuery({
    queryKeyPrefix: maxMatchKeys.miniLists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      maxMatchService.miniSearch(s, p),
  });

export const useRunMaxMatchMini = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (summaryId: number) => maxMatchService.miniRun(summaryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.miniLists() });
    },
  });
};

export const useMaxMatchMiniImages = (summaryId: number) =>
  useQuery({
    queryKey: maxMatchKeys.miniImages(summaryId),
    queryFn: () => maxMatchService.miniImages(summaryId),
    enabled: summaryId > 0,
  });

export const useMaxMatchMiniMrs = (imageId: number) =>
  useQuery({
    queryKey: maxMatchKeys.miniMrs(imageId),
    queryFn: () => maxMatchService.miniMrs(imageId),
    enabled: imageId > 0,
  });

export const useAddMaxMatchMiniMr = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      imageId,
      payload,
    }: {
      imageId: number;
      payload: MaxMatchMiniMrCreatePayload;
    }) => maxMatchService.addMiniMr(imageId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.all });
    },
  });
};

export const useAddMaxMatchMiniMrsBatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      imageId,
      payload,
    }: {
      imageId: number;
      payload: MaxMatchMiniMrCreatePayload[];
    }) => maxMatchService.addMiniMrsBatch(imageId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.all });
    },
  });
};

export const useDeleteMaxMatchMiniMr = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mrId: number) => maxMatchService.deleteMiniMr(mrId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.all });
    },
  });
};

export const useDeleteAllMaxMatchMiniMrs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) => maxMatchService.deleteAllMiniMrs(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.all });
    },
  });
};

export const useUpdateMaxMatchMiniMr = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mrId,
      payload,
    }: {
      mrId: number;
      payload: MaxMatchMiniMrUpdatePayload;
    }) => maxMatchService.updateMiniMr(mrId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.all });
    },
  });
};

export const useUpdateMaxMatchMiniPrescriptionInfo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      summaryId,
      prescriptionYearMonth,
    }: {
      summaryId: number;
      prescriptionYearMonth: string;
    }) =>
      maxMatchService.updateMiniPrescriptionInfo(summaryId, {
        prescriptionYearMonth,
      }),
    // prescription-info 는 PUT만 있음 — 목록의 처방년월·상태를 다시 맞춘다
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.miniLists() });
    },
  });
};

export const useConfirmMaxMatchMiniImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) => maxMatchService.confirmMiniImage(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.all });
    },
  });
};

export const useMarkMaxMatchMiniImageUnverifiable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) =>
      maxMatchService.markMiniImageUnverifiable(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.all });
    },
  });
};

export const useConfirmMaxMatchMiniBatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageIds: number[]) =>
      maxMatchService.miniConfirmBatch(imageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchKeys.all });
    },
  });
};

export const useDownloadMaxMatchExcel = () =>
  useMutation({
    mutationFn: (params: Record<string, string | number | undefined>) =>
      maxMatchService.getExcelDetails(params),
  });

export const useDownloadMaxMatchMiniExcel = () =>
  useMutation({
    mutationFn: (summaryId: number) =>
      maxMatchService.miniExcelDetails(summaryId),
  });
